import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Grid, Typography, WithStyles } from "@material-ui/core";
import { styles } from "./HomeView.styles";
import { BlueLink } from "../components/Links/BlueLink";
import logo_eurex from "../../files/logos/Eurex.png";
import logo_360 from "../../files/logos/360T.png";
import icon_login from "../../files/landing/person.svg";
import icon_tray from "../../files/landing/tray.svg";
import icon_mail from "../../files/landing/mail.svg";
import logo_xetra from "../../files/logos/Xetra_2020_logo.svg";
import logo_clearstream from "../../files/logos/Logo_Clearstream.png";
import { useTranslation } from "react-i18next";
import { BlueButton } from "../components/Buttons/BlueButton";
import { useHistory } from "react-router-dom";
import Container from "@material-ui/core/Container/Container";
import { withStyles } from "@material-ui/core/styles";
import { WhiteButton } from "../components/Buttons/WhiteButton";
import { useAuthContext } from "../../auth/AuthProvider";
import { HashLink as Link } from "react-router-hash-link";
import { EmbeddedVideo } from "../components/EmbeddedVideo/EmbeddedVideo";
import { getCookieConsent } from "../components/Cookies/CookieHelper";
import { requestProducts } from "../../stores/products/ProductsAction";

export interface IHomeViewProps extends WithStyles<typeof styles> {

}

const VIDEOS = [
    {
        title: "Deutsche Börse's Data Shop",
        description: "Take a look at this video about our Data Shop and learn more about the products and features it provides to you.",
        embedId: "-tqSpuolvyQ"
    },
    {
        title: "Eurex Open Interest Insights",
        description: "By adding Eurex Open Interest Insights to our comprehensive analytics suite, we offer our clients unique insights into market positioning and dynamics. Learn more about the most important features of Eurex Open Interest Insights in our new product video.",
        embedId: "gyEDR_UnAjE"
    },
    {
        title: "HHI Insights",
        description: "Learn more on how HHI Insights can provide you with deeper insights into liquidity and market imbalances.",
        embedId: "CwZ1tgptdDY"
    },
    {
        title: "Eurex Flow Insights",
        description: "The data products Eurex Flow Insights Equity + Index and Eurex Flow Insights Fixed Income provide a daily analysis of order flow by market participant groups (agent, market maker and proprietary trader)",
        embedId: "n2595XkSybA"
    },
    {
        title: "Deutsche Börse's Analytics Suite",
        description: "Our video gives a profound overview of Deutsche Börse’s most recent Analytics offering. Starting with High Precision Timestamps (HPT), through A7 and Xetra Flow Insights, to our latest Analytics product, the Herfindahl-Hirschman Index (HHI).",
        embedId: "auvZ8WlcJG4"
    },
];

const shorten = (text, maxLength) => {
    const tokens = text.split(/\s+/);
    let result = "";
    for (const t of tokens) {
        if (result.length < maxLength) {
            result += t + " ";
        }
        else {
            result += "..."
            break;
        }
    }
    return result;
}

const HomeViewPure: React.FunctionComponent<IHomeViewProps> = ({classes}: IHomeViewProps) => {
    const {t} = useTranslation();

    const history = useHistory();

    const handleClick = (path: string) => {
        history.push(path);
    };

    const createSectionCard = (value, a7 = false, width: 6 | 12 = 6) => {
        const numberOfCategories = a7products?.categories?.length + products?.categories?.length;
        const numberOfCategoriesIsOdd = numberOfCategories % 2 !== 0;
        const categoryIndex = a7 ? numberOfCategories - a7products?.categories?.length + value + 1 : value + 1
        const lgWidth = numberOfCategoriesIsOdd && categoryIndex === numberOfCategories ? 12 : width;
        const lookupCategories = (a7 ? a7products : products)?.categories;
        const descriptionMaxLength = 320;
        return lookupCategories !== undefined && lookupCategories[value]?.visibleOnFrontpage
            ? <Grid lg={lgWidth} key={value} item className={classes.sectionCard}>
                <Link to={"products?category=" + lookupCategories[value]?.id}>
                    <Card className={classes.feature}>
                        <Typography variant="h3">{lookupCategories[value]?.name}</Typography>
                        <Typography variant="body1">{lookupCategories[value]?.descriptionAlternative.length > descriptionMaxLength ? shorten(lookupCategories[value]?.descriptionAlternative, descriptionMaxLength) : lookupCategories[value]?.descriptionAlternative}</Typography>
                    </Card>
                </Link>
            </Grid>
            : null;
    };

    const Video = ({title, description, embedId, consent}) => {
        const youtubeUrl = `https://www.youtube.com/watch?v=${embedId}`;
        return <Grid container spacing={6} xs={12}>
            <Grid md={6} key="right" item>
                <div className={classes.testimonial}>
                    <Typography variant="h2">{title}</Typography>
                    <Typography variant="subtitle1">{description}</Typography>
                </div>
            </Grid>
            <Grid md={6} key="left" item className={classes.video}>
                {
                    consent === "all"
                        ? <EmbeddedVideo embedId={embedId} />
                        : <div className={classes.videoDisabled}>
                            <Typography variant="h3">Unable to preview content, unless cookies are accepted.</Typography>
                            <Typography variant="subtitle2">Alternatively follow this link: <a target="_blank" rel="noreferrer"
                                                                                               href={youtubeUrl}>{youtubeUrl}</a></Typography>
                        </div>
                }
            </Grid>
        </Grid>;
    };

    const authContext = useAuthContext();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(requestProducts());
        // eslint-disable-next-line
    }, []);

    const products = useSelector((state) => state.products);
    const a7products = useSelector((state) => state.a7products);
    const consent = getCookieConsent();

    return (
        <>
            <div className={classes.teaser}>
                <Container>
                    <div className={classes.teaserText}>
                        <Typography variant="h1">{t("home.mainHeadline")}</Typography>
                        <div className={classes.teaserButtons}>
                            <BlueButton onClick={() => handleClick("/products")}>Browse products</BlueButton>
                            {authContext.isAuthenticated
                                ? null
                                : <WhiteButton onClick={() => authContext.requestAuth()}>Login</WhiteButton>}
                        </div>
                    </div>
                </Container>
            </div>
            <div className={`${classes.section} ${classes.second}`}>
                <Container>
                    <div className={classes.productHeadline}>
                        <Typography variant="h2">Discover our self-service platform to gain easy access to detailed and accurate
                            historical market data to perform unique analyses and optimize trading strategies.
                            Our platform allows you to subscribe to daily data as well as download one-off history ad
                            hoc.</Typography>
                    </div>

                    <div className={classes.featureRow}>
                        <Grid container justify="flex-start" spacing={6}>
                            {products.categories?.map((category, index) => createSectionCard(index))}
                            {a7products.categories?.map((category, index) => createSectionCard(index, true))}
                        </Grid>
                    </div>
                </Container>
            </div>

            <div className={classes.section}>
                <Container>
                    {VIDEOS.map(v => <Video key={v.embedId}
                                            title={v.title}
                                            description={v.description}
                                            embedId={v.embedId}
                                            consent={consent}
                    />)}

                    <Grid container justify="center" alignItems="center" spacing={6} className={classes.prefooter}>
                        <Grid lg={3} item>
                            <Card className={classes.prefooterElement}>
                                <img alt="Register" height={60} src={icon_login} />
                                <BlueLink to="/register" variant="h3">Register</BlueLink>
                            </Card>
                        </Grid>
                        <Grid lg={3} item>
                            <Card className={classes.prefooterElement}>
                                <img alt="Browse products" height={60} src={icon_tray} />
                                <BlueLink to="/products" variant="h3">Browse products</BlueLink>
                            </Card>
                        </Grid>
                        <Grid lg={3} item>
                            <Card className={classes.prefooterElement}>
                                <img alt="Contact us" height={60} src={icon_mail} />
                                <BlueLink to="/contact" variant="h3">Contact us</BlueLink>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </div>

            <Container>
                <Grid container justify="center" alignItems="center" spacing={10} className={classes.logos}>
                    <Grid sm={3} item>
                        <img alt="Xetra" src={logo_xetra} />
                    </Grid>
                    <Grid sm={3} item>
                        <img alt="Eurex" width="108px" src={logo_eurex} />
                    </Grid>
                    <Grid sm={3} item>
                        <img alt="Clearstream" width="180px" src={logo_clearstream} />
                    </Grid>
                    <Grid sm={3} item>
                        <img alt="360T" width="180px" src={logo_360} />
                    </Grid>
                </Grid>
            </Container>


        </>
    );
};

export const HomeView = withStyles(styles)(HomeViewPure);
